// Typing indicator — Figma `2918:10454` (mislabeled in Figma as
// "Reply"; the rendered design is actually three animated dots inside
// a peer-style bubble).
//
// Wire-format note: per `docs/architecture/types-and-schemas.md` §4.2
// the canonical home for typing signals is the EphemeralFrame channel
// (`{ type: "typing", from, started_at, ttl_ms: 5000 }`). The host
// owns the de-dupe, expiry, and projection; this component is pure
// presentational. Hosts pass:
//   • `participants` — current typers (display name + accent color).
//   • `accessibilityLabel` — overridden by the host's i18n layer.

import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { tokens } from '../design-system/tokens';

export interface TypingParticipant {
  readonly userId: string;
  readonly displayName: string;
}

export interface TypingIndicatorProps {
  readonly participants: ReadonlyArray<TypingParticipant>;
  /** Override the default English label — handy for i18n. */
  readonly label?: string | undefined;
  readonly testID?: string | undefined;
}

const DOT_COUNT = 3;
const DOT_DELAY_MS = 180;
const DOT_DURATION_MS = 540;

export const TypingIndicator = React.memo<TypingIndicatorProps>(
  ({ participants, label, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' =
      scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    const text = useMemo(() => {
      if (label !== undefined) return label;
      if (participants.length === 0) return '';
      if (participants.length === 1) {
        return `${participants[0]!.displayName} is typing…`;
      }
      if (participants.length === 2) {
        return `${participants[0]!.displayName} & ${participants[1]!.displayName} are typing…`;
      }
      return `${participants.length} people are typing…`;
    }, [participants, label]);

    // Stagger three dot opacity loops. We keep the `Animated.Value`s on
    // a ref so the animation isn't reseeded on every render — only the
    // mount + unmount cycle starts / stops the loops.
    const dotAnims = useRef<Animated.Value[] | null>(null);
    if (dotAnims.current === null) {
      dotAnims.current = Array.from(
        { length: DOT_COUNT },
        () => new Animated.Value(0.25),
      );
    }

    useEffect(() => {
      const anims = dotAnims.current;
      if (anims === null) return;
      const loops = anims.map((value, index) =>
        Animated.loop(
          Animated.sequence([
            Animated.delay(index * DOT_DELAY_MS),
            Animated.timing(value, {
              toValue: 1,
              duration: DOT_DURATION_MS / 2,
              useNativeDriver: true,
            }),
            Animated.timing(value, {
              toValue: 0.25,
              duration: DOT_DURATION_MS / 2,
              useNativeDriver: true,
            }),
          ]),
        ),
      );
      for (const l of loops) l.start();
      return () => {
        for (const l of loops) l.stop();
      };
    }, []);

    // Render whenever there's something to show — either resolved
    // participants or a host-supplied label (the web/desktop hosts pass
    // the formatted typing subtitle directly when they only track the
    // count, not per-peer identity).
    if (participants.length === 0 && (label === undefined || label.length === 0)) {
      return null;
    }

    const bubbleStyle = [
      styles.bubble,
      // Peer-style bubble — Figma other-bubble surface #222429 (elevated).
      { backgroundColor: colors.surface.elevated },
    ];
    const dotStyle = {
      backgroundColor: colors.text.secondary,
    };
    const labelStyle: TextStyle = {
      color: colors.text.secondary,
    };

    return (
      <View
        style={styles.row}
        accessibilityRole="text"
        accessibilityLabel={text}
        testID={testID}
      >
        <View style={bubbleStyle}>
          {dotAnims.current!.map((value, index) => (
            <Animated.View
              key={index}
              style={[styles.dot, dotStyle, { opacity: value }]}
            />
          ))}
        </View>
        {text.length > 0 ? (
          <Text style={[styles.label, labelStyle]} numberOfLines={1}>
            {text}
          </Text>
        ) : null}
      </View>
    );
  },
);

TypingIndicator.displayName = 'TypingIndicator';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.xs,
  },
  bubble: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 16,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  label: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
    fontStyle: 'italic',
  },
});
